import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "../apiSlice";
import { orders } from "../../config/config";
import { buildQueryString } from "../../utils/buildQueryString";

// Create an adapter for the orders list
// 1️⃣ Adapter
const ordersAdapter = createEntityAdapter({
  selectId: (order) => order.id,
});

const initialState = ordersAdapter.getInitialState();

// 2️⃣ Slice with RTK Query
export const ordersSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getOrdersList: builder.query({
      query: (filters) => buildQueryString("orders", filters), // common utility
      transformResponse: (response) =>
        ordersAdapter.setAll(
          initialState,
          (response.data ?? []).map((item) => ({
            ...item,
            urlLabel: item.url ? item.url.split("/").pop() : "",
          })),
        ),
      providesTags: (result) => [
        { type: "Orders", id: "LIST" },
        ...(result?.ids ?? []).map((id) => ({ type: "Orders", id })),
      ],
    }),
    getOrderById: builder.query({
      query: (id) => `orders/${id}`,
      transformResponse: (response) => ({
        ...response.data,
        urlLabel: response.data?.url ? response.data.url.split("/").pop() : "",
      }),
      providesTags: (result, error, id) => [{ type: "Orders", id }],
    }),
  }),
});

// 3️⃣ Hooks
export const { useGetOrdersListQuery, useGetOrderByIdQuery } = ordersSlice;

// 4️⃣ Filter → QueryArg selector
const selectOrderFilter = (state) => state.orderFilter;
export const selectOrdersQueryArg = createSelector(
  [selectOrderFilter],
  (filter) => ({ status: filter.status, search: filter.search }),
);

// 5️⃣ Query result selector
const selectOrdersQueryResult = createSelector(
  [selectOrdersQueryArg, (state) => state],
  (queryArg, state) =>
    ordersSlice.endpoints.getOrdersList.select(queryArg)(state),
);

// 6️⃣ Data selector
const selectOrdersData = createSelector(
  [selectOrdersQueryResult],
  (queryResult) => queryResult?.data ?? initialState,
);

// 7️⃣ Entity selectors
export const {
  selectAll: selectOrders,
  selectById: selectOrderById,
  selectIds: selectOrderIds,
} = ordersAdapter.getSelectors((state) => selectOrdersData(state));
